import type { Message, ShopOrder } from "./api/types";

/**
 * The team's own progress words — the 处理状态 column CS kept by hand — in the
 * order a customer usually moves through them. The values are the server's
 * (attribution.Progress*); anything else is refused there.
 *
 * Not the funnel stage. The stage says whether someone is worth chasing and is
 * what Meta hears; progress says what is happening with them, and only a
 * colleague sets it.
 */
export const PROGRESS: Array<{ value: string; label: string }> = [
  { value: "new", label: "新询问" },
  { value: "working", label: "跟进中" },
  { value: "considering", label: "考虑中" },
  { value: "awaiting_payment", label: "待付款" },
  { value: "won", label: "已下单" },
  { value: "no_reply", label: "无回复" },
  { value: "cancelled", label: "取消" },
  { value: "invalid", label: "无效" },
  { value: "agent", label: "代理" },
];

export function progressLabel(s: string | undefined): string {
  if (!s) return "新询问";
  if (s === "lost") return "流失";
  return PROGRESS.find((p) => p.value === s)?.label ?? s;
}

/** done: a colleague wrote that day · missed: nobody did · today · later */
export type DayState = "done" | "missed" | "today" | "later";

/** Whole days since the epoch on a Malaysian calendar (UTC+8, no daylight saving). */
const mytDay = (ms: number) => Math.floor((ms + 8 * 3600e3) / 864e5);

/**
 * A customer's first week of follow-ups, Day 1 being the day they first wrote.
 * A day is done when a colleague wrote to them on it, from the console or the
 * phone; a bot reply answers but does not follow up. The server draws the
 * list's pills by the same rules (attribution.FollowupDay / SetFollowup).
 */
export function followupWeek(
  firstSeen: string | undefined,
  messages: Message[],
  now: number = Date.now(),
): { day: number; days: DayState[] } | null {
  const first = firstSeen ? Date.parse(firstSeen) : NaN;
  if (Number.isNaN(first)) return null;
  const base = mytDay(first);
  const day = mytDay(now) - base + 1;
  const wrote = new Set<number>();
  for (const m of messages) {
    if (m.direction !== "agent") continue;
    const t = Date.parse(m.at);
    if (!Number.isNaN(t)) wrote.add(mytDay(t) - base + 1);
  }
  const days: DayState[] = [];
  for (let n = 1; n <= 7; n++) {
    days.push(wrote.has(n) ? "done" : n < day ? "missed" : n === day ? "today" : "later");
  }
  return { day, days };
}

/**
 * The two check-ins after a parcel arrives (api attribution.AfterSaleArrived,
 * AfterSaleFinished): the day it came and three more, and 30 days on and seven
 * more (owner, 2026-10-08: 「只要收到包裹后 和收到包裹30天后」). They were the CRM
 * tab's Day 3/7/14/21 columns.
 */
export const AFTER_SALE: Array<{ from: number; to: number; label: string }> = [
  { from: 0, to: 3, label: "刚收到货" },
  { from: 30, to: 37, label: "快吃完了" },
];

/** A parcel the courier never scanned as delivered counts as arrived this long after it shipped. */
const UNSCANNED_MS = 5 * 864e5;

/** Sold: paid, or cash on delivery not called off — the shop's takings (attribution.OrderIndexRow.Sold). */
const soldOrder = (o: ShopOrder) =>
  o.fulfillment_status !== "cancelled" &&
  (o.payment_status === "paid" || (o.payment_method === "cod" && !["refunded", "failed", "expired"].includes(o.payment_status)));

/** When a sold order reached its customer (attribution.OrderIndexRow.ArrivedAt); NaN for one not shipped. */
function arrivedAt(o: ShopOrder): number {
  if (!soldOrder(o)) return NaN;
  const d = Date.parse(o.delivered_at || "");
  if (!Number.isNaN(d)) return d;
  const s = Date.parse(o.shipped_at || "");
  return Number.isNaN(s) ? NaN : s + UNSCANNED_MS;
}

/**
 * A buyer's check-ins, counted from the day their latest sold order reached
 * them. One is done when a colleague wrote on or after its first day and
 * before the next one began; missed once its last day passed with nothing
 * written. The server times the list's 回访 by the same rules
 * (attribution.AfterSaleCheckpoint).
 */
export function afterSale(
  orders: ShopOrder[],
  messages: Message[],
  now: number = Date.now(),
): { paid: number; day: number; days: DayState[] } | null {
  const sold = orders.filter(soldOrder);
  if (sold.length === 0) return null;
  let received = NaN;
  for (const o of sold) {
    const t = arrivedAt(o);
    if (!Number.isNaN(t) && (Number.isNaN(received) || t > received)) received = t;
  }
  if (Number.isNaN(received)) return null;
  const base = mytDay(received);
  const day = mytDay(now) - base;
  const wrote: number[] = [];
  for (const m of messages) {
    if (m.direction !== "agent") continue;
    const t = Date.parse(m.at);
    if (!Number.isNaN(t)) wrote.push(mytDay(t) - base);
  }
  const days = AFTER_SALE.map((k, i): DayState => {
    const next = AFTER_SALE[i + 1]?.from ?? Infinity;
    if (wrote.some((d) => d >= k.from && d < next)) return "done";
    if (day > k.to) return "missed";
    return day >= k.from ? "today" : "later";
  });
  return { paid: sold.length, day, days };
}

/** The one line beside the week: where today stands, and what was missed. */
export function weekLine(w: { day: number; days: DayState[] }): string {
  const missed = w.days.filter((d) => d === "missed").length;
  const tail = missed ? ` · 漏了 ${missed} 天` : "";
  if (w.day > 7) return `第一周已过${tail}`;
  return `今天 Day ${w.day}，${w.days[w.day - 1] === "done" ? "已跟进" : "还没跟进"}${tail}`;
}
